import React, { useState, useEffect} from 'react';
import { useSearchParams, useParams } from "react-router-dom";
import { CheckCircle, XCircle, Download } from "lucide-react";
import { FaMapMarkerAlt, FaCalendarAlt} from 'react-icons/fa'; // icons
import { useTranslation } from "react-i18next";
import { API_URL_BASE, URL_BASE } from './config';
import './css/ticketconfirm.css';
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default function TicketConfirmation() {
  const {eventid} = useParams(); 
  const [searchParams] = useSearchParams();
  const [confirmResult, setConfirmResult] = useState('');
  const [ticketConfirm, setTicketConfirm] = useState('');
  const [eventResult, setEventResult] = useState('');
  const { t } = useTranslation();
  const eventName = eventResult?.event?.name || "Event Name";
  const eventdDateTime = eventResult?.event?.startdatetime || "Event Time";
  const eventAddress = eventResult?.event?.address || "Event Venue";
  
  useEffect(() => {
	const orderId = searchParams.get("orderId");
	const paymentMethod = searchParams.get("paymentMethod");

	  // 1.  Don't fetch if required params are missing
	  if (!orderId || !paymentMethod) return;
	  
	  const controller = new AbortController();

	  async function confirmOrder() {

	    try {

      const response = await fetch(API_URL_BASE + `/sales.json`,{
  		method: 'POST',
  		     headers: {
  		       'Content-Type': 'application/json',
  		     },
			 signal: controller.signal,
  		     body: JSON.stringify({
  		       confirm: {
  		           token: searchParams.get('orderId'),
				   paymentMethod: searchParams.get('paymentMethod')
  		       }
  		     }),
  		   });
		   
         if (!response.ok) {
			throw new Error('Event not found');
		 } 
        
		 const data = await response.json();
		 const result = data.confirmResult;
		 
		if(result){
			setConfirmResult(result);
				// Only set tickets if payment was successful
				if (result.status === true) {
				  setTicketConfirm(result.products?.product || []);
				} else {
				  setTicketConfirm([]);
				}
			 }
		} catch (err) {
			   if (err.name !== "AbortError") {
			     console.error("Confirm order error:", err);
			     
			   }
			 }	 
		}
		
		confirmOrder();

		return () => controller.abort();
		},[searchParams]);
		
		
	useEffect(() => {
		fetch(API_URL_BASE + `/ticket.json`,{
		method: 'POST',
			     headers: {
			       'Content-Type': 'application/json',
			     },
			     body: JSON.stringify({
			       fetchEventDetailForPurchase: {
						eventId: eventid
			       }
			     }),
			   })
		   .then(res => {
		     if (!res.ok) throw new Error('Event not found');
		     return res.json();
		   })
		   .then(data => {
		     setEventResult(data.eventdetails); // Adjust based on your actual response shape						
		   })
		   .catch(err => {
		     console.error('Error loading event:', err);
		     setEventResult(null);
		   });
		
	}, [eventid]);
		
		const generatePDF = async () => {
			  if (!confirmResult?.products?.product) return;

			  const tickets = confirmResult.products.product;
			  const pdf = new jsPDF("p", "mm", "a4");
			  const pageWidth = pdf.internal.pageSize.getWidth();
			  const margin = 10;
			  const formattedDateTime = eventdDateTime
			    ? new Date(eventdDateTime).toLocaleString("en-GB", {
			        dateStyle: "medium",
			        timeStyle: "short"
			      })
			    : "Event Date";

			  /* ----------- HEADER (PAGE 1) ----------- */
			  pdf.setFontSize(22);
			  pdf.text("Ticket Confirmation", pageWidth / 2, 20, { align: "center" });

			  pdf.setFontSize(14);
			  pdf.text(`Event: ${eventName}`, margin, 40);
			  pdf.text(`Date: ${formattedDateTime}`, margin, 50);
			  pdf.text(`Venue: ${eventAddress}`, margin, 70);

			  /* Add a tickets heading */
			  pdf.setFontSize(16);
			  pdf.text("Tickets", margin, 90);

			  /* ----------- TICKETS RENDERING ----------- */
			  for (let i = 0; i < tickets.length; i++) {
			    const ticketElement = document.getElementById(`ticket-pdf-${i}`);
			    if (!ticketElement) continue;

			    const canvas = await html2canvas(ticketElement, {
			      scale: 3,
			      backgroundColor: null,
			    });

			    const imgData = canvas.toDataURL("image/png");
			    const imgWidth = pageWidth - margin * 2;
			    const imgHeight = (canvas.height * imgWidth) / canvas.width;

			    // First ticket goes on page 1 under header
			    if (i === 0) {
			      pdf.addImage(imgData, "PNG", margin, 100, imgWidth, imgHeight);

			    } else {
			      // Extra tickets begin on new pages
			      pdf.addPage();
			      pdf.addImage(imgData, "PNG", margin, 20, imgWidth, imgHeight);
			    }
			  }

			  //pdf.save(`ticket-${confirmResult.invoiceID}.pdf`);
			  return pdf.output("blob");

		};
	
		
		const downloadPDF = async () => {
		  const blob = await generatePDF();
		  if (!blob) return;

		  const url = URL.createObjectURL(blob);

		  // Mobile + Desktop friendly:
		  window.open(url, "_blank");
		};
		

	if (!confirmResult?.invoiceID) {
		return (
			<div className="processing-page">
				<div className="spinner"></div>
				 <h2>Processing your payment <span className="dots">...</span></h2>
				 <p>Please wait, do not refresh this page…</p>
			</div>
		)
	}	

  return (
	<div>
    <div className="confirmation-container">
		{/* Header based on payment status */}
		{confirmResult?.status === true ? (
		  <div className="confirmation-header">
		    <CheckCircle className="icon-success" strokeWidth={1.5} color="white"/>
		    <h1>{t("success")}</h1>
		    <p>{t("successconfirm")}</p>
		  </div>
		) : confirmResult?.status === false ? (
		  <div className="confirmation-header canceled">
		    <XCircle className="icon-error" strokeWidth={1.5} color="white" />
		    <h1>{t("status")}</h1>
		    <p>{confirmResult.message}</p>
		  </div>
		) : null}

		{/* Event info */}
		{confirmResult ? (
		<div>	
	        <div className="confirmation-event-header">
	          <h2>{eventName}</h2>
	          <p><FaCalendarAlt style={{ color: "#fff" }} />  {new Date(eventdDateTime).toLocaleString()} </p>
	          <p><FaMapMarkerAlt/> {eventAddress} </p>
	          <hr />
	          <p className="order-id">Order ID: {confirmResult.invoiceID}</p>
	        </div>
			
			{ticketConfirm?.length > 0 && (
			<div className="qr-section">
			  <p className="qr-caption" style={{ textAlign: "center", display: "block" }}>{t("qrcode")}</p>
			</div>
			)}

			{/* Actions */}
			{ticketConfirm?.length > 0 && (
			<div className="actions">
			  <button className="btn-primary" style={{ background:"#0ea5e9" }} onClick={downloadPDF}>
			    <Download size={18} /> {t("download")}
			  </button>
			</div>
			)}

		{/* Tickets (only if payment succeeded) */}
		{ticketConfirm.length > 0 && ticketConfirm.map((product,i) => {

			return (	
				<div key={product.Hjid}  className="ticketsconfirm-list">	
						{/* PDF target wrapper */}
						<div id={`ticket-pdf-${i}`} className="ticketconfirm-card">
							<div className="ticketconfirm-left">
								<QRCode
						            value={product.voucherCode}
						            size={130}
						            bgColor="#ffffff"
						            fgColor="#000000"
						            level="M"
						            includeMargin={false}
						          /> 
							</div>  
							<div className="ticketconfirm-right">	
								<div>{product.name}</div>
								<div>Ticket {t("price")} : € {(product.price/100).toFixed(2)} </div>
								<div className="ticket-right-detail">
									<div>Ticket id :  {product.voucherCode.substring(product.voucherCode.lastIndexOf("_") + 1)} </div>
									<div>Serial No :  {product.serialNumber} </div>
								</div>
							</div>
						</div>
				</div>		
		)
	   })} 
		</div>
		) : null 
		}

		
		{/* * FOOTER */}
		
		{confirmResult?.status === true ? (
		  <p className="footer-note" style={{ textAlign: "center", display: "block" }}>{t("email")}</p>
		) : confirmResult?.status === false ? (
		  <div>
		  <p className="footer-note">{t("tryagain")}</p>
		  <a style={{ textAlign: "center", display: "block" }} href= {`${URL_BASE}/buy/${eventid}`} > {t("buy")} </a>
		  </div>
		) : null}


		 
    </div>
	
	<div className='footer-copyright'>
	<span>© {new Date().getFullYear()} Event Ticketing by Buydeal.eu B.V</span>
	</div>
	
	</div>
  );
}
